// src/KhwarizmChat.js — Chat Widget Component
// This file goes in: khwarizm-website/src/KhwarizmChat.js

import React, { useState, useRef, useEffect } from 'react';

const KhwarizmChat = ({ isRTL = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [questionCount, setQuestionCount] = useState(0);
  const [showLeadCapture, setShowLeadCapture] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const language = isRTL ? 'ar' : 'en';

  const t = isRTL ? {
    button: 'اسأل خوارزم',
    buttonSub: 'قراءات تعليمية بالذكاء الاصطناعي',
    welcome: 'أهلاً بك في خوارزم. أنا مساعد تعليمي أشرح الذكاء الاصطناعي، الخوارزميات، التفكير المنهجي، وثقافة الأسواق. لا أقدّم توصيات شراء أو بيع، وكل ما أقدمه محتوى تعليمي فقط.',
    placeholder: 'اكتب سؤالك هنا...',
    send: 'إرسال',
    close: '✕',
    thinking: 'يفكّر...',
    leadTitle: 'أعجبتك المحادثة؟',
    leadDesc: 'احفظ المحادثة واستلم دليل خوارزم المجاني',
    leadEmail: 'بريدك الإلكتروني',
    leadSend: 'أرسل',
    leadSkip: 'تخطّي',
    leadThanks: 'شكراً! سنرسل لك الدليل قريباً.',
    error: 'حدث خطأ. حاول مرة أخرى.',
    disclaimer: 'محتوى تعليمي فقط — ليس توصية مالية'
  } : {
    button: 'Ask Khwarizm',
    buttonSub: 'AI-powered educational readings',
    welcome: 'Welcome to Khwarizm. I\'m an educational AI assistant for AI, algorithms, systematic thinking, and market education. I do not provide buy/sell recommendations. Everything here is educational content only.',
    placeholder: 'Type your question...',
    send: 'Send',
    close: '✕',
    thinking: 'Thinking...',
    leadTitle: 'Enjoying the conversation?',
    leadDesc: 'Save your chat and get the free Khwarizm guide',
    leadEmail: 'Your email',
    leadSend: 'Send',
    leadSkip: 'Skip',
    leadThanks: 'Thank you! We\'ll send the guide shortly.',
    error: 'Something went wrong. Please try again.',
    disclaimer: 'Educational content only — not financial advice'
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input.trim() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: newMessages.map(m => ({ role: m.role, content: m.content })),
          language
        })
      });

      const data = await res.json();

      if (data.reply) {
        setMessages([...newMessages, { role: 'assistant', content: data.reply }]);
        const newCount = questionCount + 1;
        setQuestionCount(newCount);
        if (newCount === 3 && !emailSent) {
          setTimeout(() => setShowLeadCapture(true), 1500);
        }
      } else {
        setMessages([...newMessages, { role: 'assistant', content: t.error }]);
      }
    } catch (err) {
      setMessages([...newMessages, { role: 'assistant', content: t.error }]);
    }

    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const submitEmail = async () => {
    if (!email.trim()) return;
    try {
      await fetch('https://formspree.io/f/mlgzbqoz', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          source: 'khwarizm-chat',
          language,
          questions: questionCount
        })
      });
    } catch (e) { /* fail silently */ }
    setEmailSent(true);
    setShowLeadCapture(false);
  };

  // Colors matching khwarizm.ai
  const colors = {
    dark: '#131316',
    dark2: '#1B191F',
    gold: '#C8963E',
    cream: '#F0EAE0',
    muted: '#A89B85',
    darkCard: '#1E1B22'
  };

  // Styles
  const S = {
    // Floating button
    fab: {
      position: 'fixed',
      bottom: 24,
      right: isRTL ? 'auto' : 24,
      left: isRTL ? 24 : 'auto',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 4,
      cursor: 'pointer',
      transition: 'transform 0.3s ease',
    },
    fabButton: {
      background: colors.dark,
      border: `1px solid ${colors.gold}`,
      borderRadius: 50,
      padding: '12px 24px',
      color: colors.gold,
      fontSize: 14,
      fontWeight: 600,
      cursor: 'pointer',
      fontFamily: isRTL ? 'Amiri, serif' : 'Cinzel, serif',
      letterSpacing: isRTL ? 0 : 2,
      boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
      transition: 'all 0.3s ease',
    },
    fabSub: {
      fontSize: 10,
      color: colors.muted,
      fontFamily: isRTL ? 'Amiri, serif' : 'sans-serif',
    },
    // Chat window
    window: {
      position: 'fixed',
      bottom: 24,
      right: isRTL ? 'auto' : 24,
      left: isRTL ? 24 : 'auto',
      width: 380,
      maxWidth: 'calc(100vw - 48px)',
      height: 520,
      maxHeight: 'calc(100vh - 48px)',
      background: colors.dark,
      border: `1px solid ${colors.gold}40`,
      borderRadius: 16,
      display: 'flex',
      flexDirection: 'column',
      zIndex: 10000,
      boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
      overflow: 'hidden',
      animation: 'khwChatIn 0.3s ease-out',
    },
    // Header
    header: {
      background: colors.dark2,
      padding: '14px 18px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: `1px solid ${colors.gold}30`,
    },
    headerTitle: {
      color: colors.gold,
      fontSize: 15,
      fontWeight: 600,
      fontFamily: isRTL ? 'Amiri, serif' : 'Cinzel, serif',
      letterSpacing: isRTL ? 0 : 1,
    },
    headerClose: {
      background: 'none',
      border: 'none',
      color: colors.muted,
      fontSize: 20,
      cursor: 'pointer',
      padding: '0 4px',
    },
    // Messages area
    messagesArea: {
      flex: 1,
      overflowY: 'auto',
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
    },
    // Message bubble
    msgUser: {
      background: `${colors.gold}20`,
      border: `1px solid ${colors.gold}30`,
      borderRadius: 12,
      borderBottomRight: isRTL ? 12 : 4,
      borderBottomLeft: isRTL ? 4 : 12,
      padding: '10px 14px',
      color: colors.cream,
      fontSize: 13,
      lineHeight: 1.6,
      alignSelf: isRTL ? 'flex-start' : 'flex-end',
      maxWidth: '80%',
      direction: isRTL ? 'rtl' : 'ltr',
      fontFamily: isRTL ? 'Amiri, serif' : 'inherit',
    },
    msgBot: {
      background: colors.darkCard,
      border: `1px solid ${colors.gold}15`,
      borderRadius: 12,
      borderBottomLeft: isRTL ? 12 : 4,
      borderBottomRight: isRTL ? 4 : 12,
      padding: '10px 14px',
      color: colors.cream,
      fontSize: 13,
      lineHeight: 1.7,
      alignSelf: isRTL ? 'flex-end' : 'flex-start',
      maxWidth: '85%',
      direction: isRTL ? 'rtl' : 'ltr',
      fontFamily: isRTL ? 'Amiri, serif' : 'inherit',
    },
    // Welcome message
    welcomeMsg: {
      background: 'transparent',
      border: `1px solid ${colors.gold}25`,
      borderRadius: 12,
      padding: '12px 14px',
      color: colors.muted,
      fontSize: 12,
      lineHeight: 1.7,
      textAlign: 'center',
      direction: isRTL ? 'rtl' : 'ltr',
      fontFamily: isRTL ? 'Amiri, serif' : 'inherit',
    },
    // Input area
    inputArea: {
      padding: '12px 16px',
      borderTop: `1px solid ${colors.gold}20`,
      background: colors.dark2,
      display: 'flex',
      gap: 8,
      alignItems: 'center',
    },
    input: {
      flex: 1,
      background: colors.dark,
      border: `1px solid ${colors.gold}30`,
      borderRadius: 8,
      padding: '10px 14px',
      color: colors.cream,
      fontSize: 13,
      outline: 'none',
      direction: isRTL ? 'rtl' : 'ltr',
      fontFamily: isRTL ? 'Amiri, serif' : 'inherit',
    },
    sendBtn: {
      background: colors.gold,
      border: 'none',
      borderRadius: 8,
      padding: '10px 16px',
      color: colors.dark,
      fontSize: 13,
      fontWeight: 600,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      fontFamily: isRTL ? 'Amiri, serif' : 'inherit',
    },
    // Disclaimer
    disclaimer: {
      textAlign: 'center',
      fontSize: 10,
      color: colors.muted,
      padding: '6px 12px',
      background: colors.dark,
      direction: isRTL ? 'rtl' : 'ltr',
      fontFamily: isRTL ? 'Amiri, serif' : 'inherit',
    },
    // Lead capture modal
    leadOverlay: {
      position: 'absolute',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(19,19,22,0.92)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10,
      padding: 20,
    },
    leadBox: {
      background: colors.dark2,
      border: `1px solid ${colors.gold}40`,
      borderRadius: 12,
      padding: 24,
      textAlign: 'center',
      direction: isRTL ? 'rtl' : 'ltr',
      maxWidth: 300,
      width: '100%',
    },
    leadTitle: {
      color: colors.gold,
      fontSize: 16,
      fontWeight: 600,
      marginBottom: 8,
      fontFamily: isRTL ? 'Amiri, serif' : 'Cinzel, serif',
    },
    leadDesc: {
      color: colors.muted,
      fontSize: 12,
      marginBottom: 16,
      lineHeight: 1.5,
      fontFamily: isRTL ? 'Amiri, serif' : 'inherit',
    },
    leadInput: {
      width: '100%',
      background: colors.dark,
      border: `1px solid ${colors.gold}30`,
      borderRadius: 8,
      padding: '10px 14px',
      color: colors.cream,
      fontSize: 13,
      outline: 'none',
      marginBottom: 12,
      direction: 'ltr',
      boxSizing: 'border-box',
    },
    leadBtnRow: {
      display: 'flex',
      gap: 8,
      justifyContent: 'center',
    },
    leadSendBtn: {
      background: colors.gold,
      border: 'none',
      borderRadius: 8,
      padding: '8px 20px',
      color: colors.dark,
      fontSize: 13,
      fontWeight: 600,
      cursor: 'pointer',
    },
    leadSkipBtn: {
      background: 'transparent',
      border: `1px solid ${colors.muted}50`,
      borderRadius: 8,
      padding: '8px 16px',
      color: colors.muted,
      fontSize: 12,
      cursor: 'pointer',
    },
  };

  // CSS animation (injected once)
  useEffect(() => {
    if (!document.getElementById('khw-chat-style')) {
      const style = document.createElement('style');
      style.id = 'khw-chat-style';
      style.textContent = `
        @keyframes khwChatIn {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .khw-fab:hover { transform: scale(1.05); }
        .khw-fab:hover .khw-fab-btn { background: #1B191F !important; }
        .khw-send:hover { opacity: 0.9; }
        .khw-send:disabled { opacity: 0.5; cursor: not-allowed; }
        .khw-input:focus { border-color: #C8963E60 !important; }
        .khw-msgs::-webkit-scrollbar { width: 4px; }
        .khw-msgs::-webkit-scrollbar-track { background: transparent; }
        .khw-msgs::-webkit-scrollbar-thumb { background: #C8963E30; border-radius: 4px; }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Render
  if (!isOpen) {
    return (
      <div className="khw-fab" style={S.fab} onClick={() => setIsOpen(true)}>
        <button className="khw-fab-btn" style={S.fabButton}>{t.button}</button>
        <span style={S.fabSub}>{t.buttonSub}</span>
      </div>
    );
  }

  return (
    <div style={S.window}>
      {/* Header */}
      <div style={S.header}>
        <span style={S.headerTitle}>{isRTL ? 'خوارزم' : 'Khwarizm'}</span>
        <button style={S.headerClose} onClick={() => setIsOpen(false)}>{t.close}</button>
      </div>

      {/* Messages */}
      <div className="khw-msgs" style={S.messagesArea}>
        <div style={S.welcomeMsg}>{t.welcome}</div>

        {messages.map((msg, i) => (
          <div key={i} style={msg.role === 'user' ? S.msgUser : S.msgBot}>
            {msg.content}
          </div>
        ))}

        {loading && (
          <div style={{ ...S.msgBot, opacity: 0.6, fontStyle: 'italic' }}>
            {t.thinking}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Lead Capture Overlay */}
      {showLeadCapture && (
        <div style={S.leadOverlay}>
          <div style={S.leadBox}>
            <div style={S.leadTitle}>{t.leadTitle}</div>
            <div style={S.leadDesc}>{t.leadDesc}</div>
            <input
              style={S.leadInput}
              type="email"
              placeholder={t.leadEmail}
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && submitEmail()}
            />
            <div style={S.leadBtnRow}>
              <button style={S.leadSendBtn} onClick={submitEmail}>{t.leadSend}</button>
              <button style={S.leadSkipBtn} onClick={() => setShowLeadCapture(false)}>{t.leadSkip}</button>
            </div>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div style={S.inputArea}>
        <input
          ref={inputRef}
          className="khw-input"
          style={S.input}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t.placeholder}
          disabled={loading}
        />
        <button
          className="khw-send"
          style={S.sendBtn}
          onClick={sendMessage}
          disabled={loading || !input.trim()}
        >
          {t.send}
        </button>
      </div>

      {/* Disclaimer */}
      <div style={S.disclaimer}>{t.disclaimer}</div>
    </div>
  );
};

export default KhwarizmChat;
